import React, { useEffect, useState } from "react";
import {
  List,
  ListItem,
  ListIcon,
  OrderedList,
  UnorderedList,
  Divider,
  Box,
  Text, 
  HStack,
  Input, 
  InputLeftElement,
  InputGroup,
  Skeleton,
  IconButton,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { MdCheckCircle, MdMailOutline, MdAdd, MdDelete, MdClose } from "react-icons/md";
import GmailAuth from "../../GmailAuth";
import { useNavigate } from 'react-router-dom';

export const EmailAccounts = () => {
    const [userEmails, setUserEmails] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [activeInputs, setActiveInputs] = useState({});
    const [newAliases, setNewAliases] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [editingAlias, setEditingAlias] = useState(null);
    const toast = useToast();

    const BASE_URL = process.env.REACT_APP_DEPLOYED_BASE_URL;
    const token = localStorage.getItem('token');
    const navigate = useNavigate();

    const handleAddClick = (index, e) => {
        e.stopPropagation();
        setActiveInputs(prev => ({
            ...prev,
            [index]: [...(prev[index] || []), Date.now()]
        }));
        setNewAliases(prev => ({
            ...prev,
            [index]: [...(prev[index] || []), '']
        }));
    };

    const handleAliasChange = (parentIndex, inputIndex, value) => {
        setNewAliases(prev => ({
            ...prev,
            [parentIndex]: prev[parentIndex].map((alias, idx) => 
                idx === inputIndex ? value : alias
            )
        }));
    };

    const handleAddAlias = async (parentEmail, parentIndex) => {
        const aliasesToAdd = newAliases[parentIndex]?.filter(alias => alias.trim()) || [];
        
        if (aliasesToAdd.length === 0) {
            setActiveInputs(prev => ({
                ...prev,
                [parentIndex]: []
            }));
            setNewAliases(prev => ({
                ...prev,
                [parentIndex]: []
            }));
            return;
        }
        
        setIsSaving(true);
        try {
            const response = await fetch(BASE_URL + "/alias-email/", {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    account_email: parentEmail,
                    alias_emails: aliasesToAdd
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add aliases');
            }

            const responseData = await response.json();
            
            toast({
                title: "Aliases added successfully",
                description: `Added ${aliasesToAdd.length} alias(es) to ${parentEmail}`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            setActiveInputs(prev => ({
                ...prev,
                [parentIndex]: []
            }));
            setNewAliases(prev => ({
                ...prev,
                [parentIndex]: []
            }));

            await fetchEmails();

        } catch (error) {
            toast({
                title: "Error adding aliases",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            console.error('Error adding aliases:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDoubleClick = (alias) => {
        setEditingAlias(alias);
    };

    const handleAliasEdit = async (alias, newValue) => {
        if (!newValue || newValue === alias.email_id) {
            setEditingAlias(null);
            return;
        }

        setIsSaving(true);
        try {
            const response = await fetch(BASE_URL + "/update-email-alias/", {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    old_alias: alias.email_id,
                    new_alias: newValue 
                })
            });

            if (response.ok) {
                toast({
                    title: "Alias updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                await fetchEmails();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update alias');
            }
        } catch (error) {
            toast({
                title: "Error updating alias",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
            setEditingAlias(null);
        }
    };

    const handleDeleteAlias = async (alias) => {
        setIsSaving(true);
        try {
            const aliasEmail = alias.email || alias.email_id;
            const response = await fetch(BASE_URL + "/alias-email/", {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    alias_email: aliasEmail
                })
            });

            if (response.ok) {
                toast({
                    title: "Alias deleted successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                await fetchEmails();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete alias');
            }
        } catch (error) {
            toast({
                title: "Error deleting alias",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleClickOutside = (e) => {
        if (!e.target.closest('.email-input-container')) {
            Object.entries(activeInputs).forEach(([parentIndex, inputs]) => {
                if (inputs.length > 0) {
                    const parentEmail = userEmails[parentIndex].email_id;
                    handleAddAlias(parentEmail, parseInt(parentIndex));
                }
            });
        }
        
        if (editingAlias && !e.target.closest('.alias-input-container')) {
            handleAliasEdit(editingAlias, e.target.value);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [activeInputs, newAliases, editingAlias]);

    const fetchEmails = async()=>{
        setIsLoading(true)

        try {
            const response = await fetch(BASE_URL + "/get-user-emails/",{
              headers: {
                'Authorization': `Bearer ${token}`
            }});
            
            if (response.status === 401) {
              console.error('Unauthorized: User session expired');
              localStorage.removeItem("userEmail");
              localStorage.removeItem("token");
              navigate('/login');
            };
            if (!response.ok) {
              throw new Error('Network response was not ok');
            };
            const data = await response.json();
            setUserEmails(data.User_Emails)
    
            setIsLoading(false);
            if (data) {
    
            }
    
          } catch (error) {
            setIsLoading(false);
            console.error('Error:', error);
            
          }

    }
    useEffect(()=> {
        fetchEmails()        
    },[])

  return (
    <Box px={5} mx={'auto'} className="accounts_page_outer">
        <HStack  justifyContent={'space-between'} my={10} className="add_email_sec">
            <InputGroup w={'30%'}>
                <InputLeftElement pointerEvents='none'>
                </InputLeftElement>
            </InputGroup>
            <GmailAuth payloadvalue={0} fetchEmails={fetchEmails}/>
        </HStack>
        <div className="boxScroll">
      <List spacing={3} my={10} className="box-col">
        {userEmails?
            <Skeleton isLoaded={!isLoading}>
            {userEmails.map((user, index) => {
            return (
                <>
                <ListItem key={index} display={'flex'} flexDirection="column" my={2} mx={'20px'}>
                    <HStack justifyContent={'space-between'} width="100%">
                        <HStack>
                            <ListIcon as={MdMailOutline} color="green.500" />
                            <Text>{user.email_id} ({user.total_active_campaign})</Text>
                        </HStack> 
                        <HStack>
                            <IconButton
                                aria-label="Add alias"
                                icon={<MdAdd />}
                                size="sm"
                                onClick={(e) => handleAddClick(index, e)}
                                isDisabled={isSaving}
                            />
                        </HStack> 
                    </HStack>
                    {activeInputs[index]?.map((inputId, inputIndex) => (
                        <Box key={inputId} className="email-input-container" mt={2} width="100%" ml={8}>
                            <Input
                                placeholder="Enter email alias"
                                value={newAliases[index]?.[inputIndex] || ''}
                                onChange={(e) => handleAliasChange(index, inputIndex, e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAddAlias(user.email_id, index);
                                    }
                                }}
                                autoFocus
                                isDisabled={isSaving}
                            />
                        </Box>
                    ))}
                    {user.aliases && user.aliases.map((alias, aliasIndex) => (
                        <Box key={aliasIndex} ml={8} mt={2}>
                            {editingAlias === alias ? (
                                <Input
                                    className="alias-input-container"
                                    defaultValue={alias.email_id}
                                    onBlur={(e) => handleAliasEdit(alias, e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleAliasEdit(alias, e.target.value);
                                        }
                                    }}
                                    autoFocus
                                    isDisabled={isSaving}
                                />
                            ) : (
                                <HStack>
                                    <ListIcon as={MdMailOutline} color="blue.500" />
                                    <Text 
                                        onDoubleClick={() => handleDoubleClick(alias)}
                                        cursor="pointer"
                                    >
                                        {alias.email_id} ({alias.total_active_campaign})
                                    </Text>
                                    <IconButton
                                        aria-label="Delete alias"
                                        icon={<MdDelete />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => handleDeleteAlias(alias)}
                                        isDisabled={isSaving}
                                    />
                                </HStack>
                            )}
                        </Box>
                    ))}
                    {user.alias_emails && user.alias_emails.length > 0 && user.alias_emails.map((aliasObj, idx) => (
                        <Box key={`api-alias-${idx}`} ml={8} mt={2}>
                            <HStack>
                            <IconButton
                                    aria-label="Delete alias"
                                    icon={<MdClose 
                                    style={{
                                    fontSize: "1.5rem", 
                                    fontWeight: "bold",
                                  }}
                                />}
                                    size="md"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => handleDeleteAlias(aliasObj)}
                                    isDisabled={isSaving}
                                    
                                />
                                <Text>{aliasObj.email} ({aliasObj.total_active_campaign})</Text>
                            </HStack>
                        </Box>
                    ))}
                </ListItem>
                <Divider/>
                </>
            );
            })}
            </Skeleton> : 
            <Skeleton height={'100px'}/>
        }
      </List>
      </div>
    </Box>
  );
};
